import { memo, useEffect, useMemo, useReducer, useState } from 'react';

import clsx from 'clsx';
import { SquarePen, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';

import { PartnerSpotlightForm } from '../../partnerSpotlightFormDialog/form';
import { PartnerSpotlightFormDialog } from '../../partnerSpotlightFormDialog/PartnerSpotlightFormDialog';
import { mapDtoToForm, mapFormToDto } from '../../partnerSpotlightFormDialog/utils';

import css from './PartnerSpotlightCard.module.css';

import { casinoService } from 'v2/api/casinoService';
import { partnerSpotlightService } from 'v2/api/partnerSpotlightService';
import { Popconfirm } from 'v2/components/popconfirm/Popconfirm';
import { PartnerSpotlightModel } from 'v2/types/PartnerSpotlightModel';
import { fileSrc, handleHttpError } from 'v2/utils';

interface PartnerSpotlightCardProps {
  partnerSpotlight: PartnerSpotlightModel;
  onRevalidate: VoidFunction;
}

export const PartnerSpotlightCard = memo<PartnerSpotlightCardProps>(({ partnerSpotlight, onRevalidate }) => {
  const [isOpen, setOpen] = useReducer((state) => !state, false);

  const { data: casinos } = useSWR('getCasinos', casinoService.getCasinos);
  const casino = casinos?.find((casino) => casino.id === partnerSpotlight.partnerId);

  const serverActive = partnerSpotlight.isActive !== false;
  const [activeVisual, setActiveVisual] = useState(serverActive);

  useEffect(() => {
    setActiveVisual(serverActive);
  }, [partnerSpotlight.id, serverActive]);

  const { trigger: triggerUpdate, isMutating } = useSWRMutation(
    ['updatePartnerSpotlight', partnerSpotlight.id],
    partnerSpotlightService.updatePartnerSpotlight,
    {
      onSuccess: () => {
        onRevalidate();
        toast.success('Partner Spotlight has been successfully updated');
        setOpen();
      },
      onError: handleHttpError,
    }
  );

  const { trigger: triggerToggleActive, isMutating: isTogglingActive } = useSWRMutation(
    ['togglePartnerSpotlightActive', partnerSpotlight.id],
    partnerSpotlightService.togglePartnerSpotlightActive,
    {
      onSuccess: () => {
        onRevalidate();
      },
      onError: (err) => {
        setActiveVisual(partnerSpotlight.isActive !== false);
        void handleHttpError(err);
      },
    }
  );

  const { trigger: triggerDelete } = useSWRMutation(
    ['deletePartnerSpotlight', partnerSpotlight.id],
    partnerSpotlightService.deletePartnerSpotlight,
    {
      onSuccess: () => {
        onRevalidate();
        toast.success('Partner Spotlight has been successfully deleted');
      },
      onError: handleHttpError,
    }
  );

  const handleSubmit = (values: PartnerSpotlightForm, resetForm: VoidFunction) => {
    triggerUpdate(
      mapFormToDto(values, {
        isActive: serverActive,
      })
    ).then(() => {
      resetForm();
    });
  };

  const handleActiveChange = (_event: unknown, checked: boolean) => {
    setActiveVisual(checked);
    void triggerToggleActive(checked);
  };

  const formInitialValues = useMemo(() => mapDtoToForm(partnerSpotlight), [partnerSpotlight]);

  return (
    <div className={clsx(css.partnerSpotlightCard, !activeVisual && css.partnerSpotlightCardInactive)}>
      <div
        style={{ backgroundImage: `url("${fileSrc(partnerSpotlight.backgroundImageId)}")` }}
        className={css.partnerSpotlightCardPreview}
      >
        {!activeVisual && (
          <Chip className={css.partnerSpotlightInactiveBadge} label="Inactive" size="small" color="default" />
        )}
        {!partnerSpotlight.isBackgroundOnly && casino?.transparentLogoId && (
          <img src={fileSrc(casino.transparentLogoId)} alt="Partner Logo" className={css.partnerSpotlightCardLogo} />
        )}
      </div>

      <div className={css.partnerSpotlightCardContent}>
        <div className={css.partnerSpotlightCardContentInner}>
          <p className={css.partnerSpotlightCardName}>
            {partnerSpotlight.isBackgroundOnly ? 'Background Only' : casino?.name || 'Partner Spotlight'}
          </p>

          {!partnerSpotlight.isBackgroundOnly && casino && (
            <div className={css.partnerSpotlightCardDetails}>
              {casino.benefits.freeSpinsCount !== undefined &&
                casino.benefits.freeSpinsCount !== null &&
                casino.benefits.freeSpinsCount > 0 && (
                  <Chip label={`${casino.benefits.freeSpinsCount} Free Spins`} size="small" />
                )}
              {casino.benefits.depositPercentage !== undefined &&
                casino.benefits.depositPercentage !== null &&
                casino.benefits.depositPercentage > 0 && (
                  <Chip label={`${casino.benefits.depositPercentage}% Deposit`} size="small" />
                )}
            </div>
          )}
        </div>

        <FormControlLabel
          className={css.partnerSpotlightCardActiveSwitch}
          sx={{
            '& .MuiFormControlLabel-label': {
              transition: 'color 0.2s ease',
              ...(activeVisual ? { color: 'success.main' } : {}),
            },
          }}
          control={
            <Switch
              checked={activeVisual}
              onChange={handleActiveChange}
              disabled={isTogglingActive}
              color="success"
              sx={{
                '& .MuiSwitch-switchBase': {
                  transition: (theme) =>
                    theme.transitions.create(['transform', 'background-color'], {
                      duration: theme.transitions.duration.short,
                    }),
                },
                '& .MuiSwitch-track': {
                  transition: (theme) =>
                    theme.transitions.create(['background-color', 'opacity'], {
                      duration: theme.transitions.duration.short,
                    }),
                },
              }}
            />
          }
          label={activeVisual ? 'Active' : 'Inactive'}
          labelPlacement="start"
        />

        <div className={css.partnerSpotlightCardActions}>
          <Popconfirm message="Are you sure that you want to delete this partner spotlight?" onConfirm={triggerDelete}>
            <Button startIcon={<Trash2 width={18} />} variant="outlined" color="error" fullWidth>
              Delete
            </Button>
          </Popconfirm>

          <Button onClick={setOpen} startIcon={<SquarePen width={18} />} variant="outlined" color="warning">
            Edit
          </Button>

          <PartnerSpotlightFormDialog
            title="Edit Partner Spotlight"
            isOpen={isOpen}
            onClose={setOpen}
            initialValues={formInitialValues}
            isSubmitting={isMutating}
            onSubmit={handleSubmit}
          />
        </div>
      </div>
    </div>
  );
});

PartnerSpotlightCard.displayName = 'PartnerSpotlightCard';
